import z__default from 'zod';
import { getCommonLibraryContext } from '../../context.mjs';
import { usStateOrTerritorySchema } from '../../enums.mjs';
import { REQUIRED_ERROR_MESSAGE } from '../../utils/required-error.util.mjs';
import { zipCode, city, streetAddress, EnvironmentName, GoogleMaps } from '../opaque.mjs';
import { formatAddress, fetchWithExponentialBackoff } from '../utils.mjs';

const addressSchema = z__default.object({
    street: streetAddress,
    city: city,
    state: usStateOrTerritorySchema,
    zip: zipCode,
});
const partialAddressSchema = addressSchema.partial();
const parseAddressString = (addressString) => {
    const trimmed = addressString.trim();
    if (!trimmed)
        return undefined;
    const lastCommaIndex = trimmed.lastIndexOf(",");
    if (lastCommaIndex === -1)
        return undefined;
    const streetAndCity = trimmed.slice(0, lastCommaIndex).trim();
    const stateAndZip = trimmed.slice(lastCommaIndex + 1).trim();
    const stateZipMatch = stateAndZip.match(/^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (!stateZipMatch)
        return undefined;
    const state = stateZipMatch[1];
    const zip = stateZipMatch[2];
    const lastSpaceIndex = streetAndCity.lastIndexOf(" ");
    if (lastSpaceIndex === -1)
        return undefined;
    const street = streetAndCity.slice(0, lastSpaceIndex).trim().replace(/,+$/, "");
    const city = streetAndCity.slice(lastSpaceIndex + 1).trim();
    if (!street || !city)
        return undefined;
    return {
        street,
        city,
        state,
        zip,
    };
};
const getValidatedAddress = async (address) => {
    if (!address || address.length === 0)
        return;
    const commonLibraryContext = getCommonLibraryContext();
    if (commonLibraryContext.config.environment === EnvironmentName.local ||
        commonLibraryContext.config.environment === EnvironmentName["ci-cd"] ||
        commonLibraryContext.config.environment === EnvironmentName.test) {
        const parsedAddress = parseAddressString(address);
        return parsedAddress;
    }
    const cachedAddress = await (async () => {
        if (commonLibraryContext.checkValidAddressCacheFn)
            return await commonLibraryContext.checkValidAddressCacheFn(address);
        return undefined;
    })();
    if (cachedAddress !== undefined)
        return cachedAddress;
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    const raw = JSON.stringify({
        address: {
            regionCode: "US",
            addressLines: [address],
        },
    });
    const requestOptions = {
        method: "POST",
        headers: headers,
        body: raw,
        redirect: "follow",
    };
    const url = `https://addressvalidation.googleapis.com/v1:validateAddress?key=${commonLibraryContext.config.googleAddressApiKey}`;
    const response = await fetchWithExponentialBackoff(url, requestOptions);
    if (!response.ok) {
        if (commonLibraryContext.logDataToSentry) {
            commonLibraryContext.logDataToSentry("GoogleMapsAddressValidationError", {
                response,
                address,
            });
            return;
        }
        throw new Error(`Sentry log invalid, Google Maps Address Validation API returned an error: ${response.status} ${response.statusText}`);
    }
    const parsedResponse = (await response.json());
    const postBox = parsedResponse.result.address.addressComponents?.find((component) => component.componentType === "post_box");
    const streetNumber = parsedResponse.result.address.addressComponents?.find((component) => component.componentType === "street_number");
    const route = parsedResponse.result.address.addressComponents?.find((component) => component.componentType === "route");
    const subpremise = parsedResponse.result.address.addressComponents?.find((component) => component.componentType === "subpremise");
    const neighborhood = parsedResponse.result.address.addressComponents?.find((component) => component.componentType === "neighborhood");
    const locality = parsedResponse.result.address.addressComponents?.find((component) => component.componentType === "locality");
    const subLocality = parsedResponse.result.address.addressComponents?.find((component) => component.componentType === "sublocality_level_1");
    const administrativeAreaLevel1 = parsedResponse.result.address.addressComponents?.find((component) => component.componentType === "administrative_area_level_1");
    const postalCode = parsedResponse.result.address.addressComponents?.find((component) => component.componentType === "postal_code");
    if ((postBox === undefined && (streetNumber === undefined || route === undefined)) ||
        (locality === undefined && subLocality === undefined && neighborhood === undefined) ||
        administrativeAreaLevel1 === undefined ||
        postalCode === undefined)
        return;
    if ((postBox &&
        ![
            GoogleMaps.AddressComponentsEntityConfirmationLevel.CONFIRMED,
            GoogleMaps.AddressComponentsEntityConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE,
        ].includes(postBox.confirmationLevel)) ||
        (streetNumber &&
            ![
                GoogleMaps.AddressComponentsEntityConfirmationLevel.CONFIRMED,
                GoogleMaps.AddressComponentsEntityConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE,
            ].includes(streetNumber.confirmationLevel)) ||
        (route &&
            route.confirmationLevel !== GoogleMaps.AddressComponentsEntityConfirmationLevel.CONFIRMED) ||
        (subpremise &&
            ![
                GoogleMaps.AddressComponentsEntityConfirmationLevel.CONFIRMED,
                GoogleMaps.AddressComponentsEntityConfirmationLevel.UNCONFIRMED_BUT_PLAUSIBLE,
            ].includes(subpremise.confirmationLevel)) ||
        (locality &&
            locality.confirmationLevel !==
                GoogleMaps.AddressComponentsEntityConfirmationLevel.CONFIRMED) ||
        (subLocality &&
            subLocality.confirmationLevel !==
                GoogleMaps.AddressComponentsEntityConfirmationLevel.CONFIRMED) ||
        (neighborhood &&
            neighborhood.confirmationLevel !==
                GoogleMaps.AddressComponentsEntityConfirmationLevel.CONFIRMED) ||
        administrativeAreaLevel1.confirmationLevel !==
            GoogleMaps.AddressComponentsEntityConfirmationLevel.CONFIRMED ||
        postalCode.confirmationLevel !== GoogleMaps.AddressComponentsEntityConfirmationLevel.CONFIRMED)
        return;
    const postBoxText = postBox?.componentName?.text;
    const streetNumberText = streetNumber?.componentName?.text;
    const routeText = route?.componentName?.text;
    const subpremiseText = subpremise?.componentName?.text;
    const localityText = locality?.componentName?.text;
    const subLocalityText = subLocality?.componentName?.text;
    const neighborhoodText = neighborhood?.componentName?.text;
    const stateText = administrativeAreaLevel1?.componentName?.text;
    const zipText = postalCode?.componentName?.text;
    const streetText = postBoxText ??
        (streetNumberText && routeText
            ? `${streetNumberText} ${routeText}${subpremiseText ? ` ${subpremiseText}` : ""}`
            : undefined);
    const cityText = localityText ?? subLocalityText ?? neighborhoodText;
    if (!streetText || !cityText || !stateText || !zipText)
        return;
    const validatedAddress = {
        street: streetText,
        city: cityText,
        state: stateText,
        zip: zipText,
    };
    if (commonLibraryContext.setValidAddressCacheFn) {
        await commonLibraryContext.setValidAddressCacheFn(address, validatedAddress);
    }
    return validatedAddress;
};
const getValidAddressSchema = (options) => z__default
    .union([z__default.string(), partialAddressSchema])
    .nullish()
    .pipe(z__default.transform((val, ctx) => {
    if (!options.allowNull && (val === "" || val === null || val === undefined)) {
        ctx.issues.push({
            code: "custom",
            message: REQUIRED_ERROR_MESSAGE,
            input: val,
        });
        return z__default.NEVER;
    }
    if (options.allowNull && val === "") {
        return null;
    }
    return val;
}))
    .pipe(validAddressSchema);
const validAddressSchema = z__default
    .any()
    .transform(async (val, ctx) => {
    const addAddressError = () => {
        ctx.addIssue({
            code: "custom",
            message: "Invalid address",
            input: val,
        });
    };
    if (typeof val === "string") {
        const validatedAddress = await getValidatedAddress(val);
        if (!validatedAddress) {
            addAddressError();
            return z__default.NEVER;
        }
        return validatedAddress;
    }
    if (typeof val === "object" && val !== null) {
        const unvalidatedAddress = formatAddress(val);
        const validatedAddress = await getValidatedAddress(unvalidatedAddress);
        if (!validatedAddress) {
            addAddressError();
            return z__default.NEVER;
        }
        return validatedAddress;
    }
    return val;
})
    .brand("ValidAddress");
const verifiedAddressSchema = addressSchema.brand("VerifiedAddress");
var Address;
(function (Address) {
    (function (ValidAddress) {
        ValidAddress.schema = validAddressSchema;
    })(Address.ValidAddress || (Address.ValidAddress = {}));
    (function (VerifiedAddress) {
        VerifiedAddress.schema = verifiedAddressSchema;
    })(Address.VerifiedAddress || (Address.VerifiedAddress = {}));
})(Address || (Address = {}));

export { Address, addressSchema, getValidAddressSchema, getValidatedAddress, partialAddressSchema };
